// Pahar hero — ambient self-animating sky.
//
// Ported from the React prototype (Hero.jsx + lib/arc.js + lib/color.js).
// The sun rises along a quadratic-bezier arc from dawn (left) to dusk
// (right); the sky then darkens, the stars come out, and the moon walks the
// same arc before handing the field back to dawn. Sky/body/glow tints morph
// with position, and the section background tints to match. No interaction.
//
// A section opts into the dark half by shipping the night markup
// ([data-moon-group] / [data-star-field]). Without it the loop stays the
// original dawn-to-dusk sweep, so the hero is untouched by this file.

(function () {
  'use strict';

  // The hero section and the solar footer both ship this file; only one boot.
  if (window.__paharSolarBooted) return;
  window.__paharSolarBooted = true;

  var ARC_W = 1280;
  var ARC_H = 280;
  var CYCLE_MS = 16000; // one full dawn -> dusk sweep
  var NIGHT_MS = 12000; // and the dark half that follows it

  // Where the sun's half of the loop ends and the moon's begins.
  var DAY_END = CYCLE_MS / (CYCLE_MS + NIGHT_MS);
  var FULL_MS = CYCLE_MS + NIGHT_MS;

  // Three-stop palettes (Pratah / Madhyahna / Sandhya) — see lib/tees.js.
  // The night stops read the same way: dusk / midnight / the hour before dawn.
  var PAL = {
    sky: ['#f4d6b8', '#f6f4ee', '#e8c9c3'],
    sun: ['#f0a86a', '#ffffff', '#d77a6a'],
    glow: ['#fbe7c1', '#fdfcf7', '#f0c8b8'],
    night: ['#2b2440', '#0b1022', '#1e2545'],
    moon: ['#f2e9d4', '#fdfcf7', '#e6eaf6'],
    moonGlow: ['#cbb894', '#dde5f6', '#b6c4e8'],
  };

  var INK = '#0a0a0a';
  var PAPER = '#faf9f6';

  function hexToRgb(h) {
    var n = parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r) {
    function c(n) {
      return Math.round(n).toString(16).padStart(2, '0');
    }
    return '#' + c(r[0]) + c(r[1]) + c(r[2]);
  }
  function mixHex(a, b, t) {
    var A = hexToRgb(a);
    var B = hexToRgb(b);
    return rgbToHex([
      A[0] + (B[0] - A[0]) * t,
      A[1] + (B[1] - A[1]) * t,
      A[2] + (B[2] - A[2]) * t,
    ]);
  }
  function sample(stops, t) {
    if (t <= 0.5) return mixHex(stops[0], stops[1], t / 0.5);
    return mixHex(stops[1], stops[2], (t - 0.5) / 0.5);
  }

  // Perceived lightness, 0..1 — enough to decide ink-or-paper over a colour.
  function luminance(hex) {
    var c = hexToRgb(hex);
    return (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;
  }

  // Two hex digits of alpha for an #rrggbbaa colour.
  function alphaHex(v) {
    return Math.round(clamp01(v / 255) * 255)
      .toString(16)
      .padStart(2, '0');
  }

  function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
  }
  function smoothstep(a, b, x) {
    var k = clamp01((x - a) / (b - a));
    return k * k * (3 - 2 * k);
  }

  function arcXY(t, w, h, padX, topY) {
    padX = padX || 60;
    topY = topY || -0.55;
    var p0x = padX, p0y = h - 40;
    var p1x = w / 2, p1y = h * topY;
    var p2x = w - padX, p2y = h - 40;
    var mt = 1 - t;
    var x = mt * mt * p0x + 2 * mt * t * p1x + t * t * p2x;
    var y = mt * mt * p0y + 2 * mt * t * p1y + t * t * p2y;
    return [x, y];
  }

  function setStops(grad, pairs) {
    var stops = grad.querySelectorAll('stop');
    for (var i = 0; i < pairs.length && i < stops.length; i++) {
      if (pairs[i]) stops[i].setAttribute('stop-color', pairs[i]);
    }
  }

  // Deterministic scatter — the same sky every load, so the field never
  // reshuffles between pages.
  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var SVG_NS = 'http://www.w3.org/2000/svg';

  function buildStars(field, count, fill) {
    var rnd = mulberry32(0x70616861);
    var stars = [];
    for (var i = 0; i < count; i++) {
      // The stage is overflow: visible, so the field can spill past the box.
      var x = -80 + rnd() * (ARC_W + 160);
      // Bias upward: the arc's own light owns the band near the horizon.
      var y = -70 + Math.pow(rnd(), 1.7) * (ARC_H - 40);
      // The drawn radius carries the halo too, so it runs past the core the
      // gradient's solid stop paints.
      var r = (0.6 + Math.pow(rnd(), 2) * 1.9) * 2.6;
      var node = document.createElementNS(SVG_NS, 'circle');
      node.setAttribute('cx', x.toFixed(1));
      node.setAttribute('cy', y.toFixed(1));
      node.setAttribute('r', r.toFixed(2));
      node.setAttribute('fill', fill);
      field.appendChild(node);
      stars.push({
        node: node,
        base: 0.3 + rnd() * 0.7,
        // Slow, unsynced breathing — no two stars pulse together.
        speed: 0.15 + rnd() * 0.5,
        phase: rnd() * Math.PI * 2,
      });
    }
    return stars;
  }

  function initHero(section) {
    var stage = section.querySelector('[data-pahar-sun]');
    if (!stage) return;
    var sunGroup = section.querySelector('[data-sun-group]');
    var halo = section.querySelector('[data-grad-halo]');
    var core = section.querySelector('[data-grad-core]');
    var coreCircle = section.querySelector('[data-sun-core]');

    // Night parts are optional; a section without them keeps the day loop.
    var moonGroup = section.querySelector('[data-moon-group]');
    var moonHalo = section.querySelector('[data-grad-moon-halo]');
    var moonDisc = section.querySelector('[data-moon-disc]');
    var field = section.querySelector('[data-star-field]');
    var streak = section.querySelector('[data-star-streak]');
    var hasNight = !!(moonGroup && field);

    var starFill = section.querySelector('[data-grad-star]');
    var stars = hasNight
      ? buildStars(field, 70, starFill ? 'url(#' + starFill.id + ')' : '#fdfcf7')
      : [];
    // Whether the field is currently costing anything, so the day half can
    // stop writing seventy opacities a frame into an invisible group.
    var fieldLit = true;

    var reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function render(t, elapsed) {
      var night = hasNight && t >= DAY_END;
      // Each body walks the arc over its own half of the loop.
      var u = night ? (t - DAY_END) / (1 - DAY_END) : t / (hasNight ? DAY_END : 1);
      var rise = Math.sin(Math.PI * clamp01(u));

      // How far into the dark we are. Rises through dusk, falls back to 0 at
      // the seam so the loop closes on daylight without a cut.
      // Held back until the sun is nearly down, so dusk stays gold rather
      // than turning grey while there is still a sun in the sky.
      var dark = hasNight
        ? clamp01(
            smoothstep(DAY_END - 0.03, DAY_END + 0.11, t) -
              smoothstep(0.955, 1, t)
          )
        : 0;

      // Both palettes are evaluated at the seam values on the far side of the
      // handover, so the crossfade has nothing to jump over. The one exception
      // is the day palette through the small hours: it walks back from dusk to
      // dawn so the sky comes up peach at first light instead of rewinding
      // through the rose it went down in.
      var dayU = night ? 1 - smoothstep(0.9, 1, t) : u;
      var nightU = night ? u : 0;

      var sun = sample(PAL.sun, dayU);
      var dayGlow = sample(PAL.glow, dayU);
      var dayBg = mixHex(PAPER, sample(PAL.sky, dayU), 0.55);

      var moon = sample(PAL.moon, nightU);
      var nightGlow = sample(PAL.moonGlow, nightU);
      var nightBg = sample(PAL.night, nightU);

      var glow = mixHex(dayGlow, nightGlow, dark);
      var bg = mixHex(dayBg, nightBg, dark);

      var pos = arcXY(u, ARC_W, ARC_H);
      var atX = ((pos[0] / ARC_W) * 100).toFixed(1) + '%';

      // Three layers, all present at every hour, so the handover is a pure
      // crossfade with no restyle to pop through:
      //   1. the day wash — a broad warm field the sun fills the whole sky with
      //   2. the moon's pool — small and high, sitting on the disc itself,
      //      because a moon only lights the patch it is standing in. Sized in
      //      px, not %, so a narrow footer does not stretch it into a column
      //   3. the ground, flat by day and stacked dark-over-horizon by night
      // Both washes fade out with their own body, so the jump back to the left
      // horizon at each handover happens while they are already invisible.
      var dayWash = alphaHex(0x55 * rise * (1 - dark));
      var moonWash = alphaHex(0x4a * rise * dark);
      var bgTop = mixHex(bg, '#05070f', 0.38 * dark);
      var bgFloor = mixHex(bg, '#2b3460', 0.32 * dark);

      section.style.background =
        'radial-gradient(120% 80% at ' + atX + ' 30%, ' +
        glow + dayWash + ', ' + glow + '00 62%), ' +
        'radial-gradient(380px 300px at ' + atX + ' 11%, ' +
        glow + moonWash + ', ' + glow + '00 72%), ' +
        'linear-gradient(180deg, ' + bgTop + ', ' + bgFloor + ')';

      var body = night ? moonGroup : sunGroup;
      var other = night ? sunGroup : moonGroup;
      body.setAttribute(
        'transform',
        'translate(' + pos[0] + ' ' + pos[1] + ')'
      );
      body.style.opacity = rise.toFixed(3);
      body.style.filter =
        'drop-shadow(0 8px ' + (60 - 32 * dark).toFixed(0) + 'px ' + glow + ') ' +
        'drop-shadow(0 0 ' + (30 - 16 * dark).toFixed(0) + 'px ' + glow + ')';
      if (other) other.style.opacity = '0';

      if (night) {
        if (moonHalo) setStops(moonHalo, [moon, moon, nightGlow, nightGlow]);
        if (moonDisc) moonDisc.setAttribute('fill', moon);
      } else {
        if (halo) setStops(halo, [sun, sun, dayGlow, dayGlow]);
        if (core) setStops(core, ['#ffffff', sun]);
        if (coreCircle) coreCircle.setAttribute('fill', sun);
      }

      if (hasNight) {
        // Stars breathe at their own rates; the field as a whole is gated on
        // how dark the sky has actually gone. Below that gate the field is
        // hidden outright and the per-star writes stop — which is most of the
        // loop, since the sun owns the longer half of it.
        if (dark <= 0.001) {
          if (fieldLit) {
            field.style.display = 'none';
            fieldLit = false;
          }
        } else {
          if (!fieldLit) {
            field.style.display = '';
            fieldLit = true;
          }
          var seconds = elapsed / 1000;
          for (var i = 0; i < stars.length; i++) {
            var s = stars[i];
            var twinkle = 0.72 + 0.28 * Math.sin(seconds * s.speed + s.phase);
            s.node.setAttribute(
              'opacity',
              (s.base * twinkle * dark).toFixed(3)
            );
          }
        }

        // One streak per night, a little after the moon clears the ridge.
        if (streak) {
          var run = clamp01((u - 0.34) / 0.055);
          var seen = night && run > 0 && run < 1 ? Math.sin(Math.PI * run) : 0;
          streak.setAttribute('opacity', (seen * 0.75 * dark).toFixed(3));
          streak.setAttribute(
            'transform',
            'translate(' + (150 + run * 290).toFixed(1) + ' ' +
              (-58 + run * 116).toFixed(1) + ')'
          );
        }

        // The credit bar sits on this field, so its ink has to travel too — but
        // it is judged against the *floor* colour it actually sits on, not
        // against `dark`. Any ramp between ink and paper passes through grey,
        // and grey type on a grey sky is type nobody can read, so this snaps on
        // measured luminance instead. The dissolve is a CSS transition on the
        // bar itself, which crosses the dead zone in a few hundred ms.
        var flip = luminance(bgFloor) < 0.485 ? 1 : 0;
        var fg = hexToRgb(mixHex(INK, PAPER, flip));
        section.style.setProperty(
          '--pf-rgb',
          fg[0] + ', ' + fg[1] + ', ' + fg[2]
        );
        section.style.setProperty('--pf-strong', rgbToHex(fg));
        section.style.setProperty(
          '--pf-watermark',
          'rgba(' + fg.join(', ') + ', ' + (0.06 + 0.04 * flip).toFixed(3) + ')'
        );
        // Keep the closing hairline readable once the sky is darker than ink.
        section.style.setProperty(
          '--pf-strip',
          mixHex(INK, PAPER, flip * 0.8)
        );
      }
    }

    // A day-only section keeps the original 16s sweep; only the sections that
    // ship the night markup pay for the extra dark half.
    var period = hasNight ? FULL_MS : CYCLE_MS;

    if (reduce) {
      render(hasNight ? DAY_END / 2 : 0.5, 0);
      return;
    }

    // A scrubbed preview drives render() by hand instead of the clock.
    section.paharRender = render;

    // The clock is accumulated rather than read off `now`, so parking the loop
    // while the footer is off-screen pauses the sky instead of fast-forwarding
    // it — you come back to the hour you left.
    var clock = 0;
    var base = null;
    var running = false;

    function frame(now) {
      if (!running) return;
      if (base === null) base = now - clock;
      clock = now - base;
      // The local preview page parks the loop on one phase to inspect it;
      // in the theme __paharScrub is never set and this is the clock.
      var t = typeof window.__paharScrub === 'number'
        ? window.__paharScrub
        : (clock % period) / period;
      render(t, clock);
      requestAnimationFrame(frame);
    }

    function play() {
      if (running) return;
      running = true;
      base = null;
      requestAnimationFrame(frame);
    }
    function pause() {
      running = false;
    }

    render(0, 0);

    // A footer is off-screen for most of a visit, and an ambient loop nobody
    // can see is just battery. Run it only while it is actually in view.
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        if (entries[entries.length - 1].isIntersecting) play();
        else pause();
      }, { rootMargin: '120px' }).observe(section);
    } else {
      play();
    }

    // Same argument for a backgrounded tab.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else if (isVisible(section)) play();
    });
  }

  // Cheap in-viewport test for the visibilitychange path, which has no entry
  // to read.
  function isVisible(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > -120 && r.top < (window.innerHeight || 0) + 120;
  }

  function boot() {
    var heroes = document.querySelectorAll('[data-pahar-hero]');
    for (var i = 0; i < heroes.length; i++) initHero(heroes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
